// Who has reviewed a dashboard row and how, and its unresolved threads,
// from what the dashboard already reads for attention (DASH_ATTENTION's
// timeline and threads, plus the row's review requests). Pure.
import { DASH_TIMELINE_ITEMS, type Actor, type DashAttentionInputs, type Reviewer as GhReviewer } from '../api/queries';
import { sameLogin } from './attention';

export type Verdict = 'changes' | 'approved' | 'commented';

export interface RowReviewer {
  /** A user's login, or a team's slug. */
  login: string;
  avatarUrl: string | null;
  /**
   * Their latest approval or request for changes (a later comment does not
   * undo either; a dismissal does), else `commented`; null for one who has
   * not reviewed (in the history read).
   */
  verdict: Verdict | null;
  /** A review is asked of them now: for the first time, or again after their verdict. */
  requested: boolean;
  team: boolean;
}

export interface ReviewSummary {
  /** Requests for changes first, then approvals, comments and those only asked; by login within each. */
  reviewers: RowReviewer[];
  /** Published review threads not resolved (of the first 100 GitHub returns). */
  unresolved: number;
  /** The timeline read does not reach the pull request's start (or may not): older reviews are missing. */
  partial: boolean;
}

const ORDER = (r: RowReviewer) => (r.verdict === 'changes' ? 0 : r.verdict === 'approved' ? 1 : r.verdict === 'commented' ? 2 : 3);

/**
 * The row's reviewers and unresolved threads. Reviews come from the
 * timeline the attention inputs read (its most recent items); `partial`
 * says whether older items exist. The author's own reviews and the
 * viewer's drafts are left out.
 */
export function reviewSummary(
  pr: { author: Actor | null; reviewRequests: { nodes: { requestedReviewer: GhReviewer | null }[] } },
  input: DashAttentionInputs,
): ReviewSummary {
  const byLogin = new Map<string, RowReviewer>();
  const key = (login: string) => login.toLowerCase();
  for (const item of input.timelineItems.nodes) {
    if (item.__typename !== 'PullRequestReview' || item.state === 'PENDING' || !item.author) continue;
    if (pr.author && sameLogin(item.author.login, pr.author.login)) continue;
    const had = byLogin.get(key(item.author.login));
    const verdict: Verdict =
      item.state === 'APPROVED' ? 'approved' : item.state === 'CHANGES_REQUESTED' ? 'changes' : item.state === 'DISMISSED' ? 'commented' : (had?.verdict ?? 'commented');
    byLogin.set(key(item.author.login), { login: item.author.login, avatarUrl: item.author.avatarUrl || null, verdict, requested: false, team: false });
  }
  for (const { requestedReviewer: r } of pr.reviewRequests.nodes) {
    if (!r) continue;
    const login = r.__typename === 'Team' ? r.slug : r.login;
    const had = byLogin.get(key(login));
    if (had) had.requested = true;
    else
      byLogin.set(key(login), {
        login,
        avatarUrl: r.__typename === 'Team' ? null : r.avatarUrl || null,
        verdict: null,
        requested: true,
        team: r.__typename === 'Team',
      });
  }
  const reviewers = [...byLogin.values()].sort((a, b) => ORDER(a) - ORDER(b) || key(a.login).localeCompare(key(b.login)));
  // A thread whose comments read are all the viewer's drafts is not published.
  const published = (t: DashAttentionInputs['reviewThreads']['nodes'][number]) =>
    !t.first.nodes.length || t.first.nodes.some((c) => c.pullRequestReview?.state !== 'PENDING');
  return {
    reviewers,
    unresolved: input.reviewThreads.nodes.filter((t) => !t.isResolved && published(t)).length,
    // Inputs kept from before `pageInfo` was read: fewer items than the window is the whole timeline.
    partial: input.timelineItems.pageInfo?.hasPreviousPage ?? input.timelineItems.nodes.length >= DASH_TIMELINE_ITEMS,
  };
}

/**
 * The review state the rail shows: the reviewers' (any request for
 * changes, else any approval), whatever reviews the repository requires,
 * so it agrees with the avatars. GitHub's `reviewDecision` counts only for
 * reviews not seen (`earlier`): when no summary was read; when the history
 * read is partial and GitHub has changes requested (which outranks an
 * approval seen); or when it is partial and the reviewers seen settle
 * nothing.
 */
export function reviewOutcome(s: ReviewSummary | null, decision: string | null): { state: 'approved' | 'changes' | null; earlier: boolean } {
  if (s) {
    if (s.reviewers.some((r) => r.verdict === 'changes')) return { state: 'changes', earlier: false };
    // A request for changes GitHub still counts may be older than the reviews read: it outranks an approval seen.
    if (s.partial && decision === 'CHANGES_REQUESTED') return { state: 'changes', earlier: true };
    if (s.reviewers.some((r) => r.verdict === 'approved')) return { state: 'approved', earlier: false };
    if (!s.partial) return { state: null, earlier: false };
  }
  if (decision === 'CHANGES_REQUESTED') return { state: 'changes', earlier: true };
  if (decision === 'APPROVED') return { state: 'approved', earlier: true };
  return { state: null, earlier: false };
}

/** What a row says of its review history: older reviews not read, a state from them, or approvals short of what GitHub requires. */
export function reviewNotes(s: ReviewSummary, decision: string | null, o = reviewOutcome(s, decision)): string[] {
  const notes: string[] = [];
  if (s.partial) notes.push('older reviews not loaded');
  if (o.earlier) notes.push(o.state === 'approved' ? 'approved earlier' : 'changes requested earlier');
  if (o.state === 'approved' && decision === 'REVIEW_REQUIRED') notes.push('more reviews required');
  return notes;
}
